<script setup>
// import usei18n from '@/core/util/composable/usei18n';
import {reactive, ref, defineProps, inject, computed} from 'vue';
import usePaginate from '@/core/util/composable/usePaginate';
import {APIService} from '@/core/util/services/api.service';
import States from '../constants/States';
import {navigate} from '@/core/util/helper/navigation.ts';
import Tooltip from '@/core/components/inputs/IconButton';
import DeleteConfirmationDialog from '@/core/components/dialogs/DeleteConfirmationDialog.vue';
import UserRoles from '../constants/UserRoles';

const isEmployee = computed(() => {
  return [UserRoles.ESS.id].includes(window.appGlobal.user.roleId);
});

const isAdmin = computed(() => {
  return [UserRoles.ADMIN.id].includes(window.appGlobal.user.roleId);
});

const isSupervisor = computed(() => {
  return [UserRoles.SUPERVISOR.id].includes(window.appGlobal.user.roleId);
});

const $toast = inject('toast');

const props = defineProps({
  showPaginator: {type: Boolean, default: true},
});
const http = new APIService(window.appGlobal.baseUrl, `api/onboarding`);

const defaultFilters = {
  owners: [],
  assignes: [],
  dueDate: null,
  location: [],
};

const normalizeFilters = ref({...defaultFilters});

const informationNormalizer = data => {
  return data.map(item => {
    return {
      id: item.id,
      title: item.title,
      description: item.description,
      owner: item.owner,
      status: States.find(val => val.id == item.status).label,
      _loading: item._loading,
    };
  });
};

const {
  showPaginator,
  currentPage,
  total,
  pages,
  pageSize,
  response,
  isLoading,
  execQuery,
} = usePaginate(http, {
  normalizer: informationNormalizer,
  query: normalizeFilters,
  pageSize: 15,
  toastNoRecords: false,
});

let filters = reactive({...defaultFilters});
const selected = ref(0);
const checkedItems = ref([]);
const deleteDialog = ref(null);

const deleteItems = items => {
  if (items instanceof Array) {
    http
      .deleteAll({
        ids: items,
      })
      .then(() => {
        return $toast.deleteSuccess();
      })
      .finally(() => {
        execQuery();
      });
  }
};

const onClickDelete = item => {
  deleteDialog.value.showDialog().then(confirmation => {
    if (confirmation === 'ok') {
      deleteItems([item.id]);
    }
  });
};

const onClickEdit = item => {
  navigate('/training/onboarding/{id}/edit', {id: item.id});
};

const onClickView = item => {
  navigate('/training/viewOnboarding/{id}', {id: item.id});
};

const onReset = () => {
  filters = {...defaultFilters};
};

const goToCreate = () => {
  window.location = `${window.appGlobal.baseUrl}/training/onboarding`;
};

const generateActions = (...args) => {
  const [index, , , row] = args;

  let actions = {};

  const editAction = {
    edit: {
      onClick: onClickEdit,
      component: Tooltip,
      props: {
        name: 'pencil-fill',
        title: 'Edit',
      },
    },
  };

  const viewAction = {
    view: {
      onClick: onClickView,
      component: Tooltip,
      props: {
        name: 'eye',
        title: 'Detail',
      },
    },
  };

  const deleteAction = {
    delete: {
      onClick: onClickDelete,
      component: Tooltip,
      props: {
        name: 'trash',
        title: 'Delete',
      },
    },
  };

  if (isAdmin.value || row.ownerId == window.appGlobal.user.id) {
    actions = {
      ...editAction,
      ...viewAction,
      ...deleteAction,
    };
  } else {
    actions = {
      ...viewAction,
    };
  }

  return {
    props: {
      header: {
        cellConfig: actions,
      },
    },
  };
};

const headers = ref([
  {name: 'id', title: 'Id', style: {flex: 1}},
  {name: 'title', title: 'Title', style: {flex: 1}},
  {name: 'description', title: 'Description', style: {flex: 1}},
  {name: 'owner', title: 'Owner', style: {flex: 1}},
  {name: 'status', title: 'Status', style: {flex: 1}},
  {
    name: 'actions',
    title: 'Actions',
    style: {flex: 1},
    cellType: 'oxd-table-cell-actions',
    cellRenderer: generateActions,
    /* cellConfig: {
      edit: {
        onClick: onClickEdit,
        props: {
          name: 'pencil-fill',
        },
      },
      view: {
        onClick: onClickView,
        props: {
          name: 'eye',
        },
      },
      delete: {
        onClick: onClickDelete,
        component: 'oxd-icon-button',
        props: {
          name: 'trash',
        },
      },
    }, */
  },
]);
</script>
<template>
  <div class="orangehrm-background-container">
    <slot :filters="filters" :onReset="onReset"></slot>
    <br />
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <div v-if="checkedItems > 0" class="actions">
          <oxd-text tag="span">
            {{ $t('general.n_records_selected', {count: checkedItems.length}) }}
          </oxd-text>
        </div>
        <oxd-text v-else tag="span">
          {{ $t('general.n_records_found', {count: total}) }}
        </oxd-text>
      </div>
      <div
        v-if="isAdmin || isSupervisor"
        class="orangehrm-horizontal-padding orangehrm-vertical-padding"
      >
        <oxd-button
          :disabled="!(isAdmin || isSupervisor)"
          label="Add"
          size="medium"
          icon-name="plus"
          type="button"
          display-type="secondary"
          @click="goToCreate"
        />
      </div>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :headers="headers"
          :items="response?.data"
          :selectable="true"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <DeleteConfirmationDialog ref="deleteDialog"></DeleteConfirmationDialog>
  </div>
</template>
<style lang="scss" scoped>
.orangehrm-header-container {
  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
  }

  span {
    margin-right: 20px;
  }
}
</style>
